import dayjs from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import { fmtDisplayDate } from './displayDate'
dayjs.extend(quarterOfYear)

/**
 * CB stores DATE filter defaults as a JSON-serialized DateSettings object
 * (see ControllerUtilities.GetDefaultFilterDates and CBQuery/cbDateRange.js
 * for the legacy ExtJS resolver). When the user runs a report we must send
 * back a pipe-delimited "YYYY-MM-DD|YYYY-MM-DD" range OR "$$cb_all", so we
 * resolve macros to concrete dates client-side.
 *
 * Format examples:
 *   {"DateSettings":{"tabType":0,"period":"0","simpleValue":"4","endAdjust":"month"}}      // This Month
 *   {"DateSettings":{"tabType":0,"period":"0","simpleValue":"18","endAdjust":"day"}}       // All Dates
 *   {"DateSettings":{"tabType":1,"advancedPeriod":"0","advancedUnits":"2","advancedValue":"30","advancedIncludeToday":true,"endAdjust":"day"}}  // Past 30 days
 *   {"DateSettings":{"StartDate":"2026-01-01","EndDate":"2026-12-31","tabType":2}}         // Static range
 */

export const ALL_DATES = '$$cb_all'

// Mirrors CBSimpleDateRange in legacy Enumerators.js
const SIMPLE = {
  lastHour: 7, thisHour: 1, nextHour: 20,
  lastDay: 8, thisDay: 2, nextDay: 21,
  lastWeek: 9, thisWeek: 3, nextWeek: 22,
  lastMonth: 10, thisMonth: 4, nextMonth: 23,
  lastQuarter: 11, thisQuarter: 5, nextQuarter: 24,
  lastYear: 12, thisYear: 6, nextYear: 25,
  all: 18,
  past: 26, future: 27, todayAndFuture: 28, todayAndPast: 29,
  thisMonthToDate: 31, thisYearToDate: 32,
  lastYearToDate: 33, lastYearToPresent: 34,
}

// Mirrors CBDateUnits in legacy
const UNITS = { minutes: 0, hours: 1, days: 2, weeks: 3, months: 4, quarters: 5, years: 6 }
const UNIT_NAME = { 0: 'minute', 1: 'hour', 2: 'day', 3: 'week', 4: 'month', 5: 'quarter', 6: 'year' }

const SIMPLE_LABEL = {
  [SIMPLE.lastHour]: 'Last Hour', [SIMPLE.thisHour]: 'This Hour', [SIMPLE.nextHour]: 'Next Hour',
  [SIMPLE.lastDay]: 'Yesterday', [SIMPLE.thisDay]: 'Today', [SIMPLE.nextDay]: 'Tomorrow',
  [SIMPLE.lastWeek]: 'Last Week', [SIMPLE.thisWeek]: 'This Week', [SIMPLE.nextWeek]: 'Next Week',
  [SIMPLE.lastMonth]: 'Last Month', [SIMPLE.thisMonth]: 'This Month', [SIMPLE.nextMonth]: 'Next Month',
  [SIMPLE.lastQuarter]: 'Last Quarter', [SIMPLE.thisQuarter]: 'This Quarter', [SIMPLE.nextQuarter]: 'Next Quarter',
  [SIMPLE.lastYear]: 'Last Year', [SIMPLE.thisYear]: 'This Year', [SIMPLE.nextYear]: 'Next Year',
  [SIMPLE.all]: 'All Dates',
  [SIMPLE.past]: 'Past', [SIMPLE.future]: 'Future',
  [SIMPLE.todayAndFuture]: 'Today and Future', [SIMPLE.todayAndPast]: 'Today and Past',
  [SIMPLE.thisMonthToDate]: 'This Month To Date',
  [SIMPLE.thisYearToDate]: 'This Year To Date',
  [SIMPLE.lastYearToDate]: 'Last Year To Date',
  [SIMPLE.lastYearToPresent]: 'Last Year To Present',
}

const fmt = (d) => (d && d.isValid?.() ? d.format('YYYY-MM-DD') : '')

const resolveSimpleRange = (sv, today = dayjs()) => {
  const n = parseInt(sv, 10)
  switch (n) {
    case SIMPLE.all: return { all: true, label: 'All Dates' }
    case SIMPLE.past: return { from: '', to: today.subtract(1, 'day').endOf('day') }
    case SIMPLE.future: return { from: today.add(1, 'day').startOf('day'), to: '' }
    case SIMPLE.todayAndPast: return { from: '', to: today.endOf('day') }
    case SIMPLE.todayAndFuture: return { from: today.startOf('day'), to: '' }

    case SIMPLE.thisDay: return { from: today.startOf('day'), to: today.endOf('day') }
    case SIMPLE.lastDay: return { from: today.subtract(1, 'day').startOf('day'), to: today.subtract(1, 'day').endOf('day') }
    case SIMPLE.nextDay: return { from: today.add(1, 'day').startOf('day'), to: today.add(1, 'day').endOf('day') }

    case SIMPLE.thisWeek: return { from: today.startOf('week'), to: today.endOf('week') }
    case SIMPLE.lastWeek: return { from: today.subtract(1, 'week').startOf('week'), to: today.subtract(1, 'week').endOf('week') }
    case SIMPLE.nextWeek: return { from: today.add(1, 'week').startOf('week'), to: today.add(1, 'week').endOf('week') }

    case SIMPLE.thisMonth: return { from: today.startOf('month'), to: today.endOf('month') }
    case SIMPLE.lastMonth: return { from: today.subtract(1, 'month').startOf('month'), to: today.subtract(1, 'month').endOf('month') }
    case SIMPLE.nextMonth: return { from: today.add(1, 'month').startOf('month'), to: today.add(1, 'month').endOf('month') }
    case SIMPLE.thisMonthToDate: return { from: today.startOf('month'), to: today.endOf('day') }

    case SIMPLE.thisQuarter: return { from: today.startOf('quarter'), to: today.endOf('quarter') }
    case SIMPLE.lastQuarter: return { from: today.subtract(1, 'quarter').startOf('quarter'), to: today.subtract(1, 'quarter').endOf('quarter') }
    case SIMPLE.nextQuarter: return { from: today.add(1, 'quarter').startOf('quarter'), to: today.add(1, 'quarter').endOf('quarter') }

    case SIMPLE.thisYear: return { from: today.startOf('year'), to: today.endOf('year') }
    case SIMPLE.lastYear: return { from: today.subtract(1, 'year').startOf('year'), to: today.subtract(1, 'year').endOf('year') }
    case SIMPLE.nextYear: return { from: today.add(1, 'year').startOf('year'), to: today.add(1, 'year').endOf('year') }
    case SIMPLE.thisYearToDate: return { from: today.startOf('year'), to: today.endOf('day') }
    case SIMPLE.lastYearToDate: return { from: today.subtract(1, 'year').startOf('year'), to: today.subtract(1, 'year').endOf('day') }
    case SIMPLE.lastYearToPresent: return { from: today.subtract(1, 'year').startOf('year'), to: today.endOf('day') }
    default:
      return null
  }
}

// "Past 30 days" / "Past 90 days" style — simplified mirror of getAdvancedDateRange
const resolveAdvancedRange = (s, today = dayjs()) => {
  const value = parseInt(s.advancedValue, 10) || 0
  const unitId = parseInt(s.advancedUnits, 10)
  const unit = UNIT_NAME[unitId]
  const includeToday = s.advancedIncludeToday !== false
  if (!unit || value <= 0) return null

  // 0 = past, 1 = future (matches CBDateRangeAdvancedPeriod)
  const isPast = parseInt(s.advancedPeriod, 10) === 0
  let from, to
  if (isPast) {
    to = includeToday ? today.endOf(unit) : today.subtract(1, unit).endOf(unit)
    from = today.startOf(unit).subtract(value, unit)
  } else {
    from = includeToday ? today.startOf(unit) : today.add(1, unit).startOf(unit)
    to = today.endOf(unit).add(value, unit)
  }
  return { from, to }
}

/**
 * Try to interpret a `filterDefault` string and return a resolved date filter.
 *
 * Returns one of:
 *   { all: true, label?: string }                — "$$cb_all"
 *   { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD', label?: string }
 *   null  — couldn't parse, caller should treat as empty
 */
export const resolveFilterDefault = (raw, now = dayjs()) => {
  if (raw == null) return null
  const trimmed = String(raw).trim()
  if (trimmed === '' || trimmed === ALL_DATES) return { all: true }

  // Pipe-delimited static range "YYYY-MM-DD|YYYY-MM-DD" (or empty side)
  if (trimmed.includes('|') && !trimmed.startsWith('{')) {
    const [f, t] = trimmed.split('|')
    return { from: f || '', to: t || '' }
  }

  // CB DateSettings JSON
  if (trimmed.startsWith('{')) {
    try {
      const obj = JSON.parse(trimmed)
      const s = obj.DateSettings || obj.dateSettings || obj
      if (!s) return null

      // tabType: 0 simple, 1 advanced, 2 static, 3 wildcard
      const tab = parseInt(s.tabType ?? 0, 10)

      if (tab === 2 || s.StartDate || s.EndDate) {
        return {
          from: s.StartDate ? fmt(dayjs(s.StartDate)) : '',
          to: s.EndDate ? fmt(dayjs(s.EndDate)) : '',
        }
      }

      if (tab === 1) {
        const r = resolveAdvancedRange(s, now)
        if (!r) return null
        return { from: fmt(r.from), to: fmt(r.to), label: `Past ${s.advancedValue} ${UNIT_NAME[s.advancedUnits]}s` }
      }

      // tab === 0 simple
      if (s.simpleValue != null) {
        const r = resolveSimpleRange(s.simpleValue, now)
        if (r?.all) return r
        if (!r) return null
        return {
          from: typeof r.from === 'string' ? r.from : fmt(r.from),
          to: typeof r.to === 'string' ? r.to : fmt(r.to),
          label: SIMPLE_LABEL[parseInt(s.simpleValue, 10)] || undefined,
        }
      }

      return null
    } catch {
      return null
    }
  }

  return null
}

/**
 * Convert a resolved value back to the wire format CB expects in ReportFilter.Value.
 */
export const toFilterValue = (resolved) => {
  if (!resolved) return ''
  if (resolved.all) return ALL_DATES
  return `${resolved.from || ''}|${resolved.to || ''}`
}

// --- Date-range presets (CC-49325) ------------------------------------------
// Quick ranges for the report filter panel's DATE controls. Applying a preset
// writes a LITERAL "YYYY-MM-DD|YYYY-MM-DD" range (or $$cb_all) so the user sees
// the real dates in the from/to inputs and can tweak them — same contract as
// resolveFilterDefault gives CB's own defaults.

// Bugfix 7b (Jordan, 7/2): full parity with CB core's Simple date dialog. The
// simple-range keys delegate to resolveSimpleRange — the same resolver used for
// CB's own DateSettings defaults — so behavior matches core by construction.
// QTD is kept from the CC-49325 AC (core has no quarter-to-date). Grouped so a
// ~24-option select stays scannable.
export const DATE_RANGE_PRESET_GROUPS = [
  {
    label: 'Common',
    presets: [
      { key: 'thisMonth', label: 'This Month' },
      { key: 'lastMonth', label: 'Last Month' },
      { key: 'mtd', label: 'Month to Date' },
      { key: 'qtd', label: 'Quarter to Date' },
      { key: 'ytd', label: 'Year to Date' },
      { key: 'lastYear', label: 'Last Year' },
      { key: 'all', label: 'All Dates' },
    ],
  },
  {
    label: 'Current',
    presets: [
      { key: 'today', label: 'Today' },
      { key: 'thisWeek', label: 'This Week' },
      { key: 'thisQuarter', label: 'This Quarter' },
      { key: 'thisYear', label: 'This Year' },
      { key: 'todayAndPast', label: 'Today & Past' },
      { key: 'todayAndFuture', label: 'Today & Future' },
    ],
  },
  {
    label: 'Past',
    presets: [
      { key: 'yesterday', label: 'Yesterday' },
      { key: 'lastWeek', label: 'Last Week' },
      { key: 'lastQuarter', label: 'Last Quarter' },
      { key: 'lastYearToDate', label: 'Last Year To Date' },
      { key: 'lastYearToPresent', label: 'Last Year To Present' },
      { key: 'past', label: 'Past' },
    ],
  },
  {
    label: 'Future',
    presets: [
      { key: 'tomorrow', label: 'Tomorrow' },
      { key: 'nextWeek', label: 'Next Week' },
      { key: 'nextMonth', label: 'Next Month' },
      { key: 'nextQuarter', label: 'Next Quarter' },
      { key: 'nextYear', label: 'Next Year' },
      { key: 'future', label: 'Future' },
    ],
  },
]

// Flat list kept for existing tests/consumers.
export const DATE_RANGE_PRESETS = DATE_RANGE_PRESET_GROUPS.flatMap((g) => g.presets)

// Map preset keys to the legacy CBSimpleDateRange ids resolveSimpleRange speaks.
const PRESET_SIMPLE_ID = {
  today: SIMPLE.thisDay, yesterday: SIMPLE.lastDay, tomorrow: SIMPLE.nextDay,
  thisWeek: SIMPLE.thisWeek, lastWeek: SIMPLE.lastWeek, nextWeek: SIMPLE.nextWeek,
  thisMonth: SIMPLE.thisMonth, lastMonth: SIMPLE.lastMonth, nextMonth: SIMPLE.nextMonth,
  thisQuarter: SIMPLE.thisQuarter, lastQuarter: SIMPLE.lastQuarter, nextQuarter: SIMPLE.nextQuarter,
  thisYear: SIMPLE.thisYear, lastYear: SIMPLE.lastYear, nextYear: SIMPLE.nextYear,
  mtd: SIMPLE.thisMonthToDate, ytd: SIMPLE.thisYearToDate,
  lastYearToDate: SIMPLE.lastYearToDate, lastYearToPresent: SIMPLE.lastYearToPresent,
  past: SIMPLE.past, future: SIMPLE.future,
  todayAndPast: SIMPLE.todayAndPast, todayAndFuture: SIMPLE.todayAndFuture,
}

export const presetToFilterValue = (key, now = dayjs()) => {
  if (key === 'all') return ALL_DATES
  if (key === 'qtd') return `${fmt(now.startOf('quarter'))}|${fmt(now)}`
  const simpleId = PRESET_SIMPLE_ID[key]
  if (simpleId == null) return null
  const r = resolveSimpleRange(simpleId, now)
  if (!r) return null
  if (r.all) return ALL_DATES
  return `${typeof r.from === 'string' ? r.from : fmt(r.from)}|${typeof r.to === 'string' ? r.to : fmt(r.to)}`
}

/**
 * Which preset a quick-range select shows as selected for `value` (CC-49870): the first
 * preset in `groups`, in order, whose wire value is exactly `value`; '' (the placeholder)
 * when none is, and for an empty value or $$cb_all.
 *
 * `groups` is the list the select OFFERS, never the whole vocabulary. A report that
 * drops presets (`omitAll`, spec 214's `omitPresets`) must not have a range that equals
 * a dropped preset read back as that preset; it reads as the placeholder instead.
 * Extracted from ReportFilterPanel's DateFilter (spec 214 wave 4) so that rule can be
 * tested directly: in the DOM, a key with no <option> also falls back to the
 * placeholder, so no assertion on the rendered select could tell the two apart.
 * ReportFilterPanel.test.jsx pins the panel's half (which list it passes) on the call.
 *
 * `now` is optional and is passed straight to presetToFilterValue, so a caller that
 * omits it resolves each preset against the current clock exactly as before.
 */
export const presetKeyFor = (value, groups, now) => {
  if (!value || value === ALL_DATES) return ''
  for (const g of groups) {
    for (const p of g.presets) {
      if (presetToFilterValue(p.key, now) === value) return p.key
    }
  }
  return ''
}

/**
 * Human-readable summary of a filter value (for the "applied filters" pill bar).
 *
 * DISPLAY ONLY, and the ONE producer behind all of it: every date filter pill on all
 * 140 reports and the collapsed filter-panel summary (via
 * `src/components/reports/filterValueSummary.js:32`), plus — on the 119 generic reports
 * — the applied-criteria chip row (`ReportView.jsx:789`) and everything built from those
 * chips, which is `filtersSummary` (`:858`) and therefore the client CSV/XLSX/PDF
 * "Filters —" header (`:1044`), the print masthead (`:1200`) and the AI scope line
 * (`:1398` → `aiScope.js`). The 21 specialized pages reach those last four through
 * `reportFilterSummary.js` instead, which already said ' to ' — see the separator note
 * below; that is the divergence this closes. It reads the halves that `toFilterValue`
 * already produced and they stop here — nothing this function returns goes back to CB.
 *
 * Halves render MM/DD/YYYY via `fmtDisplayDate` (Himaad UI feedback, 2026-08-27).
 * Before this, users read raw ISO wire dates — "2026-04-01 → 2026-04-30" — in the
 * chrome while the filter inputs immediately beside them showed MM/DD/YYYY
 * (`UsDateInput`, forced US format since CC-49824).
 *
 * The separator is ' to ', not '→', and that ends a real divergence rather than being a
 * taste call: the 21 specialized pages have always said ' to '
 * (`src/components/reports/reportFilterSummary.js`, which records that jsPDF's WinAnsi
 * Helvetica cannot encode the arrow — it prints as mojibake), and `exportClientPdf.js`'s
 * `winAnsiSafe` already rewrites the arrow to 'to' — but the client CSV/XLSX path does
 * NOT sanitize, so one run's PDF said 'to' while its spreadsheet shipped a raw arrow.
 * One separator now, in one place, for every surface listed above.
 *
 * Branch selection stays on the RAW halves so the shape of the summary can never change
 * with a formatting outcome; a non-empty half that won't parse falls through to the
 * verbatim value rather than emit a dangling "From ".
 */
export const dateFilterSummary = (value) => {
  if (!value || value === ALL_DATES) return 'All dates'
  const parts = String(value).split('|')
  if (parts.length === 2) {
    const [from, to] = parts
    const f = fmtDisplayDate(from)
    const t = fmtDisplayDate(to)
    if ((from && !f) || (to && !t)) return value
    if (!from && to) return `Up to ${t}`
    if (from && !to) return `From ${f}`
    if (from && to) return `${f} to ${t}`
  }
  return value
}
